import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { map } from 'rxjs/operators'
import { Observable } from 'rxjs'
import { OperationService, OperationServiceScheduleChacheMap, QuestionsOperationService, OperationServiceScheduleDetails, OperationServiceDataToReport } from '../models/OperationService'
import { PATH } from '../app.api'
import { OperationReport, OperationServiceReport, OperationServiceScheduleReport } from '../models/OperationReport'
import { Inventory } from '../models/Inventory'
import { Address } from '../models/Address'
import { NotificationService } from './notification.service'
import * as FileSaver from 'file-saver'
import { InventoryDtoScheduling } from '../components/protected/scheduler-inventory/pre-schedule/dto/inventory-pre-scheduling.dto'
import { RequestDto } from './base/request.base.dto'
import { PreScheduleOperationServiceDto } from '../components/protected/operation-service/pre-scheduling/dto/pre-scheduling.dto'

@Injectable()
export class OperationServiceService {

	constructor(
		private http: HttpClient,
		private readonly notificationService: NotificationService
	) {}

	/************************************************************
	 * ROTINAS DE AGENDAMENTO
	 ***********************************************************/

	public getListOperationsForScheduling(
		companyId: String, dateStart?: any, dateEnd?: any
	): Observable<Array<OperationService>> {
		const options: HttpHeaders = this.setHeader()

		return this.http
			.get<any>(`${PATH}/operationService/scheduling/all?companyId=${companyId}&${dateStart ? `&dateStart=${dateStart}&dateEnd=${dateEnd}` : ''}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getOperationsPreScheduling(operationsIds: string, companyId: string, page: number, perPage: number): Observable<{ operations: Array<PreScheduleOperationServiceDto>, count: number}> {
		const options: HttpHeaders = this.setHeader()

		return this.http
			.get<{ operations: Array<PreScheduleOperationServiceDto>, count: number}>(`${PATH}/operationService/pre-scheduling/${companyId}?operations=${operationsIds}&page=${page}&perPage=${perPage}`, {
				headers: options
			})
	}

	public updateOperationsFromScheduler(
		operationsModified: Array<PreScheduleOperationServiceDto>,
		companyId: string
	) {
		const options: any = this.setHeader()

		return this.http.patch<any>(`${PATH}/operationService/scheduler/update/${companyId}`,
			{
				preScheduleOperationsService: operationsModified
			}
			, {
				headers: options
			}).pipe(
				map(result => {
					return result.message
				})
			)
	}

	public updateOperationReportArboreal(operation: OperationServiceReport, operadorId): Observable<string> {
		const options: any = this.setHeader()

		return this.http.patch<any>(`${PATH}/operationService/reportArboreal/${operadorId}`, JSON.stringify(operation), {
			headers: options
		}).pipe(
			map(result => {
				return result.message
			})
		)
	}

	public getAddresses(operationServicesIds: Array<string>) {
		const companyId = localStorage.getItem('companyId')
		const headers: HttpHeaders = this.setHeader()
		return this.http.get(`${PATH}/operationService/addresses/${companyId}?operationsIds=${operationServicesIds.join(',')}`, {
			headers
		}).pipe(
			map((result: any) => {
				return result.content
			})
		)
	}

	public listOperatorForSchedulingDate({companyId, date, lat, lng}): Observable<any> {
		const options: any = this.setHeader()

		return this.http
			.patch<any>(
				`${PATH}/operationService/scheduling/operators`,
				{companyId, date, lat, lng},
				{ headers: options }
			)
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getCoordinatesOperationScheduler(companyId): Observable<any> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(
				`${PATH}/operationService/coordinates-operation-scheduler/${companyId}`,
				{ headers: options }
			)
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public assignOperatorToOperation(
		operations: Array<string>,
		operatorId: string,
		current_day: string,
		userCreatorId: string
	): Observable<any> {
		return this.http.patch<any>(
			`${PATH}/operationService/scheduling/assignOperatorToOperation`,
			{
				operations,
				operatorId,
				date: current_day,
				userCreatorId
			},
			{ headers: this.setHeader() }
		)
	}

	public cancelOperation(
		operationId: string,
		justifyCancel: string
	): Observable<any> {
		return this.http.patch<any>(
			`${PATH}/operationService/scheduling/${operationId}`,
			{justification: justifyCancel},
			{ headers: this.setHeader() }
		)
	}

	public getOperationServiceSelected(idMongo: string): Observable<OperationServiceScheduleDetails> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/operationService/operation-service-details/${idMongo}`, {
				headers: options
			}).pipe(
				map(result => {
					return result.content
				})
			)
	}

	public deleteOperation(operationId): Observable<any> {
		const options: HttpHeaders = this.setHeader()

		return this.http
			.delete<any>(`${PATH}/operationService/delete/${operationId}`,
			{ headers: options}
		).pipe(
			map(result => {
				return result
			})
		)
	}

	public closeOperation(operationId: string): Observable<string> {
		const options: HttpHeaders = this.setHeader()

		return this.http
			.patch<any>(`${PATH}/operationService/close/${operationId}`,
			{ headers: options}
		).pipe(
			map(result => {
				return result.message
			})
		)
	}

	/************************************************************
	 * ROTINAS DE HISTÓRICO
	 ***********************************************************/

	public getClickScheduleTree(demandId: string, companyId: string): Observable<any> {
		const options = this.setHeader()
		return this.http.get<any>(`${PATH}/operationservice/schedule/getClick/${companyId}/${demandId}`, {
			headers: options
		}).pipe(
			map(result => {
				return result.content
			}
		))
	}

	public getClickTree(demandId: string, companyId: string): Observable<any> {
		const options = this.setHeader()
		return this.http.get<any>(`${PATH}/operationservice/getClick/${companyId}/${demandId}`, {
			headers: options
		}).pipe(
			map(result => {
				return result.content[0]
			}
		))
	}

	public updateLocationPinMapSchedule(operationServiceId: string, location: Address): Observable<void> {
		const options: any = this.setHeader()

		return this.http
			.patch<any>(`${PATH}/operationService/schedule/maps/updatePin/${operationServiceId}`, JSON.stringify(location), {
				headers: options
			})
			.pipe(
				map(result => {
					return result
				})
			)
	}

	public updateLocationPinMapHistory(demandId: string, location: Address): Observable<OperationService> {
		const options: any = this.setHeader()

		return this.http
			.patch<any>(`${PATH}/operationService/maps/updatePin/${demandId}`, JSON.stringify(location), {
				headers: options
			})
			.pipe(
				map(result => {
					return result
				})
			)
	}

	public getOperationsHistory(
		companyId: string
	): Observable<Array<OperationService>> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/operationservice/history/company/${companyId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getQuestionStateTree(companyId: string): Observable<any> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/operationservice/questions/${companyId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getScheduleQuestionStateTree(companyId: string): Observable<QuestionsOperationService> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/operationservice/schedule/questions/${companyId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getOperationServiceScheduleReportArboreal(
		operationServiceScheduleIds: string
	): Observable<Array<OperationServiceScheduleReport>> {
		const options: any = this.setHeader()
		return this.http
			.get<any>(`${PATH}/operationService/operationServiceScheduleReportArboreal/${operationServiceScheduleIds}`,
			{
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getOperationReportArboreal(
		operationsData: Array<OperationServiceDataToReport>
	): Observable<Array<OperationServiceReport>> {
		const options: any = this.setHeader()

		return this.http
			.post<any>(
				`${PATH}/operationService/reportArboreal`, { operationsData } ,
				{ headers: options }
			)
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public updateScheduleMapCache(companyId: string, userEmail: string): Observable<Array<OperationServiceScheduleChacheMap>> {
		const options: any = this.setHeader()

		return this.http
			.patch<any>(`${PATH}/operationService/schedule/${companyId}/${userEmail}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public updateMapCache(companyId: string, userEmail: string): Observable<Array<any>> {
		const options: any = this.setHeader()

		return this.http
			.patch<any>(`${PATH}/operationService/${companyId}/${userEmail}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getExportOperationServicesHistory(companyId: string, dateStart?: number, dateEnd?: number): Observable<any> {
		const options: any = this.setHeader()

		const params = new HttpParams()
						.set('companyId', companyId)
			.set('dateStart', String(dateStart))
			.set('dateEnd', String(dateEnd))

		return this.http
			.get<any>(`${PATH}/operationService/history/all`, {
				headers: options,
				params
			})
			.pipe(
				map(result => {
					const stringBase64 = result.content

				 if (stringBase64 == undefined) {
					return this.notificationService.notify('Não há dados a serem exportados', 420)
				}
				// Decodificar a string Base64 para um array de bytes
				 const byteCharacters = atob(stringBase64)
				 const byteNumbers = new Array(byteCharacters.length)

				 for (let i = 0; i < byteCharacters.length; i++) {
				  byteNumbers[i] = byteCharacters.charCodeAt(i)
				}

				 const byteArray = new Uint8Array(byteNumbers)

				 const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

				// Realizar o download do arquivo automaticamente
				 FileSaver.saveAs(blob, `Relatório_de_histórico_de_manejo_export_${new Date().getTime()}.xlsx`)
				})
			)
	}

	public getSpecies(data: {
		filter?: string,
		filterId?: string
	}): Observable<any> {
		const options: any = this.setHeader()

		try {
			const queryParam = data.filterId ? `?filterId=${data.filterId}` : data.filter ? `?filter=${data.filter}` : ''

			return this.http.get(`${PATH}/species/filter${queryParam}`, options).pipe(
				map(result => {
					return result
				})
		  	)
		} catch (err) {
			console.log(err)
		}
	}

	public getInventoriesSchedule(inventoriesIds: Array<string>, actualPage: number, perPage: number):
		Observable<{message: string, inventories: Array<InventoryDtoScheduling>, countInventories: number}> {
		const options = this.setHeader()
		const companyId = localStorage.getItem('companyId')

		return this.http
			.post<any>
				(`${PATH}/inventorys/scheduler/${companyId}?page=${actualPage}&perPage=${perPage}`, {
					idsMongo: inventoriesIds
				}, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}


	public getExportOperationServicesSchedule(companyId: string, dateStart?: number, dateEnd?: number): Observable<any> {
		const options: any = this.setHeader()

		const params = new HttpParams()
						.set('companyId', companyId)
			.set('dateStart', String(dateStart))
			.set('dateEnd', String(dateEnd))

		return this.http
			.get<any>(`${PATH}/operationService/schedule/all`, {
				headers: options,
				params
			})
			.pipe(
				map(result => {
					const stringBase64 = result.content

				 if (stringBase64 == undefined) {
					return this.notificationService.notify('Não há dados a serem exportados', 420)
				}
				// Decodificar a string Base64 para um array de bytes
				 const byteCharacters = atob(stringBase64)
				 const byteNumbers = new Array(byteCharacters.length)

				 for (let i = 0; i < byteCharacters.length; i++) {
				  byteNumbers[i] = byteCharacters.charCodeAt(i)
				}

				 const byteArray = new Uint8Array(byteNumbers)

				 const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

				// Realizar o download do arquivo automaticamente
				 FileSaver.saveAs(blob, `Relatório_de_agendamento_de_manejo_export_${new Date().getTime()}.xlsx`)
				})
			)
	}

	public getOperationServiceScheduleMapForCompany(companyId: string): Observable<Array<OperationServiceScheduleChacheMap>> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/operationService/schedule/company/${companyId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public setCompanyProviderToOperationService(
		operationServiceId: string,
		companyServiceProviderId: string
	): Observable<string> {
		const options: any = this.setHeader()

		return this.http.patch<any>(`${PATH}/operationService/set-company-provider`,
			{
				operationServiceId,
				companyServiceProviderId
			},
			{ headers: options }
		).pipe(
			map(result => {
				return result.message
			})
		)
	}

		private setHeader(): HttpHeaders {
		return new HttpHeaders({ 'Content-Type': 'application/json' }).append( 'x-source-from', 'PAINEL')
	}
}
